import { Box, Container, Flex } from 'theme-ui';
import React, { forwardRef, RefObject } from 'react';
import CoverButtons from './CoverButtons';
import CoverBodyAdditionalSection from './CoverBodyAdditionalSection';
import mappedList from '~/utils/map-object-of-array-to-list';
import ShowSpecificTitle from './ShowSpecificTitle';
import LiteCarousel from '../LiteCarousel';

interface CoverBodyProps {
  contentColor?: string | null;
  mobileButtonLayout?: Queries.CoverFeatureFragmentFragment['mobileButtonLayout'];
  titleLarge?: Queries.CoverFeatureFragmentFragment['titleLarge'];
  titleMobile?: string | null;
  titleSmall?: Queries.CoverFeatureFragmentFragment['titleSmall'];
  body?: Queries.CoverFeatureFragmentFragment['body'];
  callToAction?: Queries.CoverFeatureFragmentFragment['callToAction'];
  subHeaderList?: Queries.CoverFeatureFragmentFragment['subHeaderList'];
  subtitleList?: Queries.CoverFeatureFragmentFragment['subtitleList'];
  subHeaderImages?: Queries.CoverFeatureFragmentFragment['subHeaderImages'];
  fullHeight?: Queries.CoverFeatureFragmentFragment['fullHeight'];
  alignment?: Queries.CoverFeatureFragmentFragment['alignment'];
  verticalAlignment?: Queries.CoverFeatureFragmentFragment['verticalAlignment'];
  alignItems?: string;
  separateCopy?: boolean;
  paused?: boolean;
  handleClick?: () => void;
  backgroundColor?: string | null;
  noMedia?: boolean;
  copyHeightRef?: RefObject<HTMLDivElement>;
}

interface SectionItem {
  subHeader?: string | null;
  subtitle?: string | null;
  subHeaderImage?: any; // Type depends on your image structure
}

const CoverBody = forwardRef<HTMLDivElement, CoverBodyProps>(
  (
    {
      contentColor,
      mobileButtonLayout,
      titleLarge,
      titleMobile,
      titleSmall,
      body,
      callToAction,
      subHeaderList,
      subtitleList,
      subHeaderImages,
      fullHeight,
      alignment,
      verticalAlignment,
      alignItems,
      separateCopy,
      paused,
      handleClick,
      backgroundColor,
      noMedia,
      copyHeightRef,
    },
    ref
  ) => {
    // Takes a list of keys, and arrays to match those keys
    const arrayOfSections = mappedList({
      // @ts-ignore
      subHeader: subHeaderList,
      // @ts-ignore
      subHeaderImage: subHeaderImages,
      // @ts-ignore
      subtitle: subtitleList,
    });
    const mobileTitle = titleMobile ?? titleLarge;

    function showSectionOrSlideShowSection(arrayOfSections: SectionItem[]) {
      if (!arrayOfSections || arrayOfSections.length <= 0) return null;
      return (
        <>
          <div
            sx={{
              borderTop: '1px solid #E1E1E1',
              width: ['36rem', '66.8rem'],
              mt: ['3.6rem', '4.8rem'],
              position: 'relative',
            }}
          />
          {arrayOfSections.length > 1 ? (
            <LiteCarousel
              arrows={true}
              dots={true}
              transitionSpeed={1.5}
              autoplaySpeed={4000}
              forwardSx={{
                '.dots-list': {
                  bottom: '-.5rem',
                },
                '.arrows': { display: ['none', 'flex'], contentVisibility: ['hidden', 'auto'] },
                mb: '4.2rem',
                width: '92vw',
              }}
              slides={arrayOfSections.map((object, i) => {
                const { subHeader, subtitle, subHeaderImage } = object ?? {};
                return (
                  <div
                    key={`${subHeader ?? subtitle}-${i}`}
                    sx={{ height: 'fit-content', position: 'relative', mb: '4.2rem' }}
                  >
                    <CoverBodyAdditionalSection
                      // @ts-ignore
                      slideId={`${subHeader ?? subtitle}-${i}`}
                      subHeader={subHeader}
                      subHeaderImage={subHeaderImage}
                      renderImageSpace={true}
                      subtitle={subtitle}
                      alignItems={alignItems}
                      alignment={alignItems}
                    />
                  </div>
                );
              })}
            />
          ) : (
            <CoverBodyAdditionalSection
              subHeader={arrayOfSections?.[0]?.subHeader}
              subHeaderImage={arrayOfSections?.[0]?.subHeaderImage}
              subtitle={arrayOfSections?.[0]?.subtitle}
              alignItems={alignItems}
              alignment={alignItems}
            />
          )}
        </>
      );
    }

    // If vertical alignment info is present then place the coverbody accordingly.
    const verticalInfo = typeof verticalAlignment === 'number' && !isNaN(verticalAlignment);
    const top = verticalInfo ? `${verticalAlignment}%` : 'unset';
    const topTransform = verticalInfo ? `translateY(-${verticalAlignment}%)` : 'unset';
    return (
      <Container
        className="content"
        sx={{
          mt: verticalInfo ? 0 : separateCopy && !noMedia && ['2.4rem', '4rem'],
          mb: verticalInfo ? 0 : separateCopy && ['2.4rem', '4rem'],
          display: 'flex',
          backgroundColor: backgroundColor,
          flexDirection: 'column',
          justifyContent: 'center',
          position: verticalInfo ? 'absolute' : separateCopy ? 'relative' : 'absolute',
          top: top,
          bottom: verticalInfo ? 'unset' : !separateCopy && ['2.4rem', null, '4.8rem'],
          transform: topTransform,
          width: '100%',
          maxWidth: '100%',
          px: ['2.4rem', null, '4rem'],
          py: verticalInfo ? ['2.4rem', null, '4rem'] : 0,
          zIndex: 1,
          color: contentColor,
          '@supports not (height: 100dvh)': {
            bottom: verticalInfo ? 'unset' : !separateCopy && ['6.4rem', '4.8rem'],
          },
        }}
        ref={ref}
      >
        <Flex
          ref={copyHeightRef}
          sx={{
            flexDirection: 'column',
            alignItems: alignItems ?? 'start',
            width: '100%',
            opacity: paused ? 1 : 0,
            visibility: paused ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease, visibility 0.2s ease',
            gap: '0.8rem',
          }}
        >
          <ShowSpecificTitle
            titleLarge={titleLarge}
            titleSmall={titleSmall}
            mobileTitle={mobileTitle}
            alignment={alignment}
            separateCopy={separateCopy}
            hasBody={!!body}
          />

          {body && (
            <Box
              variant="text.copy"
              as={!titleSmall && !mobileTitle ? 'h1' : 'div'}
              sx={{
                mb: '0.8rem',
                'p, h1, h2, h3, h4, h5, h6': {
                  m: 0,
                },
                lineHeight: 1.2,
                display: 'block',
                // @ts-ignore
                textAlign: alignment?.toLowerCase() ?? 'left',
                maxWidth: separateCopy ? ['36.6rem', '66.8rem'] : !fullHeight && ['31.3rem', '52.4rem'],
              }}
              dangerouslySetInnerHTML={{ __html: body?.childMarkdownRemark?.html ?? '' }}
            />
          )}

          {callToAction && (
            <CoverButtons
              callToAction={callToAction}
              handleClick={handleClick}
              contentColor={contentColor}
              mobileHorizontal={mobileButtonLayout}
            />
          )}
          {showSectionOrSlideShowSection(arrayOfSections)}
        </Flex>
      </Container>
    );
  }
);

export default CoverBody;
